import { CloseOutlined } from "@ant-design/icons";
import { Button, Flex, Form, Input, Tooltip, Typography } from "antd";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import AdvancedTable, {
  AddButton,
  AdvancedTableRef,
  EditableColumnType,
  getActionColumns,
  getIdColumn,
} from "../components/AdvancedTable";
import { ContextType, DataProvider, DataType } from "../contexts/DataProvider";
import { AnimationConfig } from "../shared/types/Animation";
import { EffectConfig } from "../shared/types/Effect";
import { dataCall } from "../tsrpc";

const { Title, Text } = Typography;

type VisualResourceConfig = DataType & {
  _id: string;
  name: string;
  path: string;
  remark?: string;
};

type VisualResourceManagementPanelProps<T extends VisualResourceConfig> = {
  context: ContextType<T>;
  collection: string;
  title: string;
  onBack?: () => void;
  closeButton?: boolean;
};

const EffectDataContext = createContext<ContextType<EffectConfig> | null>(null);

export default function PageEffects() {
  return (
    <DataProvider<EffectConfig>
      context={EffectDataContext}
      collection="effects"
      limit={100000}
      cacheKey="page:effects"
    >
      <PageEffectsInner />
    </DataProvider>
  );
}

function useEffectData() {
  const context = useContext(EffectDataContext);
  if (!context) throw new Error("useEffectData must be used within EffectDataProvider");
  return context;
}

function PageEffectsInner() {
  return <EffectManagementPanel context={useEffectData()} />;
}

export function EffectManagementPanel({
  context,
  onBack,
  closeButton,
}: {
  context: ContextType<EffectConfig>;
  onBack?: () => void;
  closeButton?: boolean;
}) {
  return (
    <VisualResourceManagementPanel
      context={context}
      collection="effects"
      title="特效管理"
      onBack={onBack}
      closeButton={closeButton}
    />
  );
}

export function AnimationManagementPanel({
  context,
  onBack,
  closeButton,
}: {
  context: ContextType<AnimationConfig>;
  onBack?: () => void;
  closeButton?: boolean;
}) {
  return (
    <VisualResourceManagementPanel
      context={context}
      collection="animations"
      title="动画管理"
      onBack={onBack}
      closeButton={closeButton}
    />
  );
}

function VisualResourceManagementPanel<T extends VisualResourceConfig>({
  context,
  collection,
  title,
  onBack,
  closeButton,
}: VisualResourceManagementPanelProps<T>) {
  const tableRef = useRef<AdvancedTableRef<T>>(null);
  const [editingData, setEditingData] = useState<T | undefined>();
  const [version, setVersion] = useState(0);

  useEffect(() => {
    getVersion();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getVersion = async () => {
    const res = await dataCall("editor/DataFind", {
      collection: "versions",
      filter: { collection },
      limit: 1,
    });
    setVersion(res.res?.data?.[0]?.version ?? 0);
  };

  const columns: EditableColumnType<T>[] = [
    getIdColumn<T>(),
    {
      title: "名称",
      dataIndex: "name",
      width: 220,
      editor: <Input />,
      formItemProps: {
        rules: [{ required: true, message: "请输入名称！" }],
      },
    },
    {
      title: "资源路径",
      dataIndex: "path",
      editor: <Input />,
      formItemProps: {
        rules: [{ required: true, message: "请输入资源路径！" }],
      },
    },
    {
      title: "备注",
      dataIndex: "remark",
      editor: <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />,
    },
    getActionColumns({
      editingData,
      setEditingData,
      display: (data) => data?.name || data?.path || data?._id,
      editSubmit: () => tableRef.current?.editSubmit,
      $delete: () => tableRef.current?.delete,
    }),
  ];

  return (
    <AdvancedTable<T>
      ref={tableRef}
      editingData={editingData}
      context={context}
      title={() => (
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={10}>
            <Title level={5} style={{ margin: 0 }}>{title}</Title>
            <Text type="secondary">{`[数据版本：${version}]`}</Text>
          </Flex>
          <Flex gap={10}>
            <AddButton<T>
              title="新增"
              add={() => context.add}
              createValue={() => ({ name: "", path: "", remark: "" } as T)}
            >
              <Form.Item
                name="name"
                label="名称"
                rules={[{ required: true, message: "请输入名称！" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="path"
                label="资源路径"
                rules={[{ required: true, message: "请输入资源路径！" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="remark" label="备注">
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
              </Form.Item>
            </AddButton>
            {closeButton && (
              <Tooltip title="关闭">
                <Button icon={<CloseOutlined />} onClick={onBack} />
              </Tooltip>
            )}
          </Flex>
        </Flex>
      )}
      columns={columns}
    />
  );
}
